import { createContext, useCallback, useContext, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const ConfirmContext = createContext(null);

const DEFAULTS = {
  title: "Are you sure?",
  description: "This action cannot be undone.",
  confirmText: "Confirm",
  cancelText: "Cancel",
  destructive: false,
};

export function ConfirmProvider({ children }) {
  const [state, setState] = useState({ ...DEFAULTS, open: false, resolve: null });

  const confirm = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      setState({ ...DEFAULTS, ...opts, open: true, resolve });
    });
  }, []);

  const close = (result) => {
    setState((s) => {
      s.resolve?.(result);
      return { ...s, open: false, resolve: null };
    });
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog open={state.open} onOpenChange={(o) => !o && close(false)}>
        <AlertDialogContent data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{state.title}</AlertDialogTitle>
            <AlertDialogDescription>{state.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-dialog-cancel" onClick={() => close(false)}>
              {state.cancelText}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-dialog-confirm"
              onClick={() => close(true)}
              className={state.destructive ? "bg-red-600 hover:bg-red-700 text-white" : "bg-brand hover:bg-brand-dark text-white"}
            >
              {state.confirmText}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

export const useConfirm = () => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used within ConfirmProvider");
  return ctx;
};
